import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Breakpoints,
  BreakpointWrapper,
  Container,
  ContainerGutter,
  ContainerPadding,
  Layout,
  Text,
  TextAlignment,
  TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import { Engine, Powertrain, TestDrive } from '@volkswagen-onehub/icons-core';

type Feature = {
  title: string;
  description: string;
  icon: React.ReactNode;
};

const FEATURES: Feature[] = [
  {
    title: 'CONDUCCIÓN',
    description:
      'El eje delantero cuenta con un bloqueo del diferencial auto blocante (VAQ) con control electrónico. En combinación con la dirección progresiva, permiten trasladar toda la potencia del motor al asfalto con una alta precisión.',
    icon: <Engine variant="default" aria-hidden />,
  },
  {
    title: 'SUSPENSIÓN',
    description:
      'La suspensión adaptativa DCC cuenta con un tarado desarrollado específicamente para esta versión, con un tacto más preciso y deportivo, y una altura rebajada en 15mm. También podrás personalizar la dureza según tus preferencias.',
    icon: <Powertrain variant="default" aria-hidden />,
  },
  {
    title: 'FRENOS',
    description:
      'Toda potencia necesita ser controlada. Este modelo cuenta con un potente sistema de frenos, compuesto por discos delanteros sobredimensionados de 357mm y pinzas de freno de doble pistón, recubiertas en color rojo y con el logotipo GTI.',
    icon: <TestDrive variant="default" aria-hidden />,
  },
];

const SectionContainer = styled(Container)`
  width: 100%;
  height: 100vh;
  align-content: center;
  justify-content: center;
  overflow: visible;

  background-image: url('https://assets.volkswagen.com/is/image/volkswagenag/detail-gti-background?Zml0PWNyb3AlMkMxJndpZD0xOTIwJmhlaT0xMDgwJmZtdD1wbmctYWxwaGEmYmZjPW9mZiY1M2Qx');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  color: #ffffff;

  @media screen and (max-width: 960px) {
    height: 100%;
  }
  margin-top: -1px;
`;

const StyledLabel = styled(Text)`
  font-family: 'Futura Condensed Extra Bold', Futura, Arial, sans-serif;
  font-weight: 800;
  font-style: Condensed ExtraBold;
  leading-trim: NONE;
  line-height: 126%;
  letter-spacing: 0%;
  color: #c90010;
`;

const StyledValue = styled(Text)`
  font-family: Futura, Arial;
  font-weight: 500;
  font-style: Medium;
  leading-trim: NONE;
  line-height: 120%;
  letter-spacing: 0%;
  color: #ffffff;
`;

const InfoHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
`;

const IconStyled = styled.div`
  & svg {
    width: clamp(40px, 6vw, 80px);
    height: clamp(40px, 6vw, 80px);
    color: #c90010;
  }
`;

const InfoTitle = styled(Text)`
  font-family: 'Futura Condensed Extra Bold', Futura, Arial, sans-serif;
  font-weight: 800;
  font-style: Condensed ExtraBold;
  leading-trim: NONE;
  line-height: 100%;
  letter-spacing: 0%;
  color: #ffffff;
`;

const InfoTextContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex: 1;
  min-height: 100px;

  @media screen and (max-width: 560px) {
    min-height: 140px;
  }
`;

const InfoText = styled(Text)`
  font-family: 'VW Head', sans-serif;
  font-weight: 400;
  font-style: normal;
  line-height: 140%;
  letter-spacing: 0%;
  text-align: center;
  color: #ffffff;
`;

const Dots = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;

  @media screen and (max-width: 560px) {
    gap: 25px;
  }
`;

const Dot = styled.button<{ active?: boolean }>`
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 0;
  background: ${({ active }) => (active ? '#ffffff' : '#C90010')};
  transform: ${({ active }) => (active ? 'scale(1.2)' : 'scale(1)')};
  transition:
    transform 0.2s ease,
    background 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: scale(1.15);
  }
  @media screen and (max-width: 560px) {
    width: 16px;
    height: 16px;
  }
`;

const GtiTitle = styled.img`
  width: auto;
  height: clamp(80px, 10vh, 140px);
  object-fit: contain;

  @media screen and (min-width: 960px) {
    position: absolute;
    left: 4%;
    margin-top: -10vh;
    height: clamp(150px, 15vh, 220px);
    z-index: 10;
  }
`;

const VideoWrapDesktop = styled.div`
  width: 100%;
  height: 100%;
  aspect-ratio: 16 / 9;
`;

const VideoWrapMobile = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  display: grid;
  place-items: center;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const MobileContainer = styled(Container)`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

export const DetailGti50 = (): React.ReactElement => {
  const [tab, setTab] = useState(0);
  const [pausedUntil, setPausedUntil] = useState<number>(0);

  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      if (now >= pausedUntil) {
        setTab((prev) => (prev + 1) % FEATURES.length);
      }
    };

    const interval = window.setInterval(tick, 5000);
    return () => {
      if (interval) window.clearInterval(interval);
    };
  }, [pausedUntil]);

  return (
    <SectionContainer
      padding={{
        top: ContainerPadding.dynamic0300,
        bottom: ContainerPadding.dynamic0300,
      }}
    >
      <BreakpointWrapper max={Breakpoints.b960}>
        <MobileContainer
          padding={{ left: ContainerPadding.grid001, right: ContainerPadding.grid001 }}
          wrap={'always'}
          gutter={ContainerGutter.static350}
        >
          <VideoWrapMobile>
            <Video
              src="https://assets.volkswagen.com/is/content/volkswagenag/video-gti2-0x720-3000k"
              autoPlay
              muted
              loop
              playsInline
            />
          </VideoWrapMobile>

          <GtiTitle
            src="https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-gti50/gti-titular-detail.png"
            alt="GTI 50"
          />

          <BreakpointWrapper max={Breakpoints.b560}>
            <Container gutter={ContainerGutter.static350} stretchContent>
              <Container wrap={'always'} gutter={'static200'}>
                <StyledLabel appearance={TokenTextAppearance.copy200}>POTENCIA</StyledLabel>
                <StyledValue appearance={TokenTextAppearance.label100}>239kW (325CV)</StyledValue>
              </Container>

              <Container wrap={'always'} gutter={'static200'}>
                <StyledLabel appearance={TokenTextAppearance.copy200}>ACELERACIÓN</StyledLabel>
                <StyledValue appearance={TokenTextAppearance.label100}>
                  5,5 segundos <br />
                  de 0 a 100km/h
                </StyledValue>
              </Container>

              <Container wrap={'always'} gutter={'static200'}>
                <StyledLabel appearance={TokenTextAppearance.copy200}>PAR MOTOR</StyledLabel>
                <StyledValue appearance={TokenTextAppearance.label100}>420Nm</StyledValue>
              </Container>
            </Container>
          </BreakpointWrapper>

          <BreakpointWrapper min={Breakpoints.b560}>
            <Container gutter={ContainerGutter.static350} stretchContent>
              <Container wrap={'always'} gutter={'static200'}>
                <StyledLabel appearance={TokenTextAppearance.headline200}>POTENCIA</StyledLabel>
                <StyledValue>239kW (325CV)</StyledValue>
              </Container>

              <Container wrap={'always'} gutter={'static200'}>
                <StyledLabel appearance={TokenTextAppearance.headline200}>ACELERACIÓN</StyledLabel>
                <StyledValue>
                  5,5 segundos <br />
                  de 0 a 100km/h
                </StyledValue>
              </Container>

              <Container wrap={'always'} gutter={'static200'}>
                <StyledLabel appearance={TokenTextAppearance.headline200}>PAR MOTOR</StyledLabel>
                <StyledValue>420Nm</StyledValue>
              </Container>
            </Container>
          </BreakpointWrapper>

          <Container wrap={'always'}>
            <Container
              padding={ContainerPadding.dynamic0100}
              gutter={ContainerGutter.static350}
              horizontalAlign={'center'}
            >
              <InfoHeader>
                <IconStyled>{FEATURES[tab].icon}</IconStyled>
                <InfoTitle appearance={TokenTextAppearance.headline300} textAlign={TextAlignment.center}>
                  {FEATURES[tab].title}
                </InfoTitle>
              </InfoHeader>

              <InfoTextContainer>
                <InfoText appearance={TokenTextAppearance.headline100}>{FEATURES[tab].description}</InfoText>
              </InfoTextContainer>

              <Dots>
                {FEATURES.map((f, i) => (
                  <Dot
                    key={f.title}
                    active={i === tab}
                    aria-label={`Cambiar a ${f.title}`}
                    onClick={() => {
                      setTab(i);
                      setPausedUntil(Date.now() + 15000);
                    }}
                  />
                ))}
              </Dots>
            </Container>
          </Container>
        </MobileContainer>
      </BreakpointWrapper>

      <BreakpointWrapper min={Breakpoints.b960}>
        <GtiTitle
          src="https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-gti50/gti-titular-detail.png"
          alt="GTI 50"
        />
        <Layout
          appearance={{
            [Breakpoints.default]: [
              {
                name: '.',
                columns: 1,
              },
              {
                name: 'a',
                columns: 8,
              },
              {
                name: '.',
                columns: 1,
              },
              {
                name: 'b',
                columns: 13,
              },
              {
                name: '.',
                columns: 1,
              },
            ],
          }}
        >
          <Container padding={{ top: ContainerPadding.dynamic0300 }} wrap={'always'} gutter={ContainerGutter.static350}>
            <Container gutter={ContainerGutter.static350} stretchContent>
              <Container wrap={'always'} verticalAlign={'center'} gutter={'static200'}>
                <StyledLabel appearance={TokenTextAppearance.headline200}>POTENCIA</StyledLabel>
                <StyledValue>239kW (325CV)</StyledValue>
              </Container>

              <Container wrap={'always'} verticalAlign={'center'} gutter={'static200'}>
                <StyledLabel appearance={TokenTextAppearance.headline200}>ACELERACIÓN</StyledLabel>
                <StyledValue>
                  5,5 segundos <br />
                  de 0 a 100km/h
                </StyledValue>
              </Container>

              <Container wrap={'always'} verticalAlign={'center'} gutter={'static200'}>
                <StyledLabel appearance={TokenTextAppearance.headline200}>PAR MOTOR</StyledLabel>
                <StyledValue>420Nm</StyledValue>
              </Container>
            </Container>

            <Container wrap={'always'}>
              <Container
                padding={ContainerPadding.dynamic0100}
                gutter={ContainerGutter.static350}
                horizontalAlign={'center'}
              >
                <InfoHeader>
                  <IconStyled>{FEATURES[tab].icon}</IconStyled>
                  <InfoTitle appearance={TokenTextAppearance.headline300} textAlign={TextAlignment.center}>
                    {FEATURES[tab].title}
                  </InfoTitle>
                </InfoHeader>

                <InfoTextContainer>
                  <InfoText appearance={TokenTextAppearance.headline100}>{FEATURES[tab].description}</InfoText>
                </InfoTextContainer>

                <Dots>
                  {FEATURES.map((f, i) => (
                    <Dot
                      key={f.title}
                      active={i === tab}
                      aria-label={`Cambiar a ${f.title}`}
                      onClick={() => {
                        setTab(i);
                        setPausedUntil(Date.now() + 15000);
                      }}
                    />
                  ))}
                </Dots>
              </Container>
            </Container>
          </Container>

          <VideoWrapDesktop>
            <Video
              src="https://assets.volkswagen.com/is/content/volkswagenag/video-gti2-0x720-3000k"
              autoPlay
              muted
              loop
              playsInline
            />
          </VideoWrapDesktop>
        </Layout>
      </BreakpointWrapper>
    </SectionContainer>
  );
};
